import { queueMacrotask } from 'module/helpers/macrotask';
import { useTranslation } from 'module/hooks/use-translation';
import { AddOptionsInput } from 'module/ui/components/atoms/choices/add-option';
import { SortableOptions } from 'module/ui/components/atoms/choices/list';
import { Form } from 'module/ui/components/atoms/form';
import { StyledItem } from 'module/ui/components/atoms/form/form';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { TableDataType } from 'module/ui/components/atoms/rich-text-editor/toolbar/table-toolbar';
import { Label01 } from 'module/ui/components/styles';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { InputField, Select, Button, Textarea, Checkbox } from '@rocketlane/ui';
import { Divider } from 'antd';
import {
  answerFrom,
  AnswerLength,
} from 'api/custom-section/types/multi-question';
import { useEffect, useRef, useState, useCallback, useMemo, FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import {
  ChoicesContainer,
  CheckboxContainer,
} from 'components/organisms/document-editor/extensions/custom-section/ui/sections-type/extract-info/styles';
import { AnswerDropdowns } from 'components/organisms/document-editor/extensions/custom-section/ui/shared/answer-dropdowns';
import { SharedConfigurationBar } from 'components/organisms/document-editor/extensions/custom-section/ui/shared/configuration-bar';
import { SectionHeading } from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-heading';
import {
  SectionCard,
  SectionDivider,
  TextAreaContainer,
} from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-styles';
import { ConfigurationBar } from '../../../shared/section-styles';
import type { Option } from '@rocketlane/ui';
import type { GroupedFilter } from 'module/types/filter-field/grouped-filter/filter';

const Label = styled.div`
  ${Label01};
  align-self: flex-start;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;
const CloseIcon = styled(Button).attrs({
  variant: 'ghost',
})`
  margin-top: 0 !important;
  margin-bottom: 0 !important;
`;
const StyledChoicesContainer = styled(ChoicesContainer)`
  .ant-form-item-control-input-content {
    align-items: center;
  }
  textarea.ant-input {
    resize: vertical !important;
    margin-top: 0 !important;
  }

  .ant-form-item-required::before {
    display: none !important;
  }
`;

const SectionWrapper = styled.div`
  padding: var(--spacing-05) 0;
  ${Form.Item} {
    padding: 0 var(--spacing-05);
  }
`;

const ConfigurationBarWrapper = styled(ConfigurationBar)<{
  hasOptions?: boolean;
}>`
  padding: 0 var(--spacing-05) 0 var(--spacing-05);
  width: 100%;
  align-items: end;
  justify-content: end;
  padding-top: ${({ hasOptions }) => (hasOptions ? '0' : 'var(--spacing-05)')};
`;

export const StyledCheckboxContainer = styled(CheckboxContainer)`
  padding-top: var(--spacing-05);
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  max-width: 100%;
  flex-direction: column;
  margin-bottom: var(--spacing-05);
  gap: var(--spacing-05);

  ${StyledItem} {
    width: 100%;
    padding-bottom: 0;

    .ant-form-item-control-input-content {
      display: flex;
    }
  }

  .ant-divider {
    margin: 0 var(--spacing-05);
  }
`;

const StyledTitleFormItem = styled(Form.Item)`
  padding-bottom: var(--layout-01) !important;
`;

const ChoiceWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: var(--spacing-03);
  width: 100%;
`;

const StyledTextArea = styled(Textarea)`
  width: 100%;
  padding: var(--spacing-04);
`;

const StyledChoicesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
  width: 100%;
`;

const StyledFormItem = styled(Form.Item)`
  .ant-form-item-control-input-content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-02);
    width: 100%;
  }

  .rl-textarea-wrapper {
    width: 100%;
  }
`;

const FormOption: FC<any> = ({
  option,
  removeOption,
  editInputOption,
  onAddOptionInputBlur,
  form,
  index,
  inputType = 'text',
  setChoices,
  focusOptionValue,
  onFocusOptionDone,
  addQuestionPlaceholder,
}) => {
  const choicesLabel = useTranslation(
    'rich_text_editor_sections_extract_info_section.choices',
  );
  const textLabel = useTranslation('fields.label.text');
  const numberLabel = useTranslation('fields.label.number');
  const booleanLabel = useTranslation('fields.label.boolean');
  const dateLabel = useTranslation('fields.label.date');
  const singleSelectLabel = useTranslation('fields.label.single_select');
  const multiSelectLabel = useTranslation('fields.label.multi_select');
  const aiGeneratedChoicesLabel = useTranslation(
    'rich_text_editor.sections.extract_info_section.ai_generated_choices',
  );
  const aiGeneratedChoicesPlaceholder = useTranslation(
    'rich_text_editor.sections.extract_info_section.ai_generated_choices_description',
  );
  const choiceTypeList = useMemo(
    () => [
      { value: TableDataType.TEXT, label: textLabel },
      { value: TableDataType.NUMBER, label: numberLabel },
      { value: TableDataType.BOOLEAN, label: booleanLabel },
      { value: TableDataType.DATE, label: dateLabel },
      { value: TableDataType.SingleSelect, label: singleSelectLabel },
      { value: TableDataType.MultiSelect, label: multiSelectLabel },
    ],
    [
      textLabel,
      numberLabel,
      booleanLabel,
      dateLabel,
      singleSelectLabel,
      multiSelectLabel,
    ],
  );

  const aiContextRef = useRef<HTMLTextAreaElement>(null);
  const questionTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const questionInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (focusOptionValue == null || option.value !== focusOptionValue) {
      return;
    }

    const focusInput = () => {
      const el =
        inputType === 'textarea'
          ? questionTextAreaRef.current
          : questionInputRef.current;

      if (el) {
        el.focus();
        const len = (el.value ?? '').length;

        el.setSelectionRange(len, len);
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        onFocusOptionDone?.();
      }
    };

    queueMacrotask(focusInput);
  }, [focusOptionValue, option.value, onFocusOptionDone, inputType]);

  const contextPlaceholder = useTranslation(
    'rich_text_editor.sections.section.context_placeholder',
  );

  // Initialize form field with current choiceType value
  useEffect(() => {
    if (form) {
      form.setFieldsValue({
        [`choiceType_${option.value}`]:
          choiceTypeList.find(
            (type) => type.value === option.fieldOptionMeta?.optionType,
          ) ?? choiceTypeList.find((type) => type.value === TableDataType.TEXT),
        [`aiContext_${option.value}`]: option.fieldOptionMeta?.aiContext,
      });
    }
  }, [
    form,
    option.fieldOptionMeta?.optionType,
    choiceTypeList,
    option.value,
    option.fieldOptionMeta?.aiContext,
  ]);

  const style = {
    opacity: 1,
    border: 'none',
  };

  const requiredMessage = useTranslation('input.required');
  const [dataChoices, setDataChoices] = useState(
    option.fieldOptionMeta?.choices ?? [],
  );

  const addDataOption = useCallback(
    (dataOption) => {
      const newOptions = dataChoices.concat({
        ...dataOption,
        value:
          (dataChoices.reduce(
            (max, dataOption) => Math.max(max, dataOption.value),
            0,
          ) ?? 0) + 1,
        position: dataChoices.length + 1,
      });

      setDataChoices(newOptions);
      editInputOption({
        ...option,
        fieldOptionMeta: {
          ...option.fieldOptionMeta,
          choices: newOptions,
        },
      });
    },
    [dataChoices, setDataChoices, editInputOption, option],
  );
  const removeDataOption = useCallback(
    (dataOption) => {
      const newOptions = dataChoices.filter(
        (x) => x.value !== dataOption.value,
      );

      const updatedOptions = newOptions.map((x, index) => ({
        ...x,
        position: index + 1,
      }));

      setDataChoices(updatedOptions);
      editInputOption({
        ...option,
        fieldOptionMeta: {
          ...option.fieldOptionMeta,
          choices: updatedOptions,
        },
      });
    },
    [dataChoices, setDataChoices, editInputOption, option],
  );
  const updateDataOptions = (dataOptions) => {
    const existingChoice = dataChoices.find(
      (existing) => existing.value === dataOptions.value,
    );

    if (existingChoice) {
      editInputOption({
        ...option,
        fieldOptionMeta: {
          ...option.fieldOptionMeta,
          choices: dataChoices.map((choice) => {
            if (choice?.value === existingChoice?.value) {
              return dataOptions;
            }

            return choice;
          }),
        },
      });
      setDataChoices((previous) => {
        return previous.map((choice) => {
          if (choice?.value === existingChoice?.value) {
            return dataOptions;
          }

          return choice;
        });
      });
    }
  };

  return (
    <FieldRow style={style}>
      <StyledFormItem
        name={option.value.toString()}
        rules={[{ required: true, message: requiredMessage }]}
      >
        <Label>
          {`${(index ?? 0) + 1}. Question`}
          <CloseIcon onClick={removeOption}>
            <FontAwesomeIcon icon={faClose} />
          </CloseIcon>
        </Label>
        {inputType === 'textarea' ? (
          <ChoiceWrapper>
            <StyledTextArea
              ref={questionTextAreaRef}
              id={option.value.toString()}
              rows={1}
              placeholder={
                option.label === '' ? addQuestionPlaceholder : undefined
              }
              onChange={(e) => {
                if (form) {
                  form.setFieldsValue({
                    [option.value]: e.target.value,
                  });

                  form
                    .validateFields([option.value.toString()])
                    .catch((error) => {
                      form.setFields([
                        {
                          name: option.value.toString(),
                          errors: error.errorFields[0].errors,
                        },
                      ]);
                    });
                }

                if (editInputOption) {
                  editInputOption({ ...option, label: e.target.value });
                }
              }}
              onBlur={() => onAddOptionInputBlur?.(option.value.toString())}
              value={option.label}
              autoFocus={option.value === focusOptionValue}
              // onKeyDown={onAddOptionInputKeyDown}
            />
          </ChoiceWrapper>
        ) : (
          <InputField
            ref={questionInputRef}
            placeholder={
              option.label === '' ? addQuestionPlaceholder : undefined
            }
            onChange={(e) => {
              if (form) {
                form.setFieldsValue({
                  [option.value]: e.target.value,
                });

                form
                  .validateFields([option.value.toString()])
                  .catch((error) => {
                    form.setFields([
                      {
                        name: option.value.toString(),
                        errors: error.errorFields[0].errors,
                      },
                    ]);
                  });

                return (
                  editInputOption &&
                  editInputOption({ ...option, label: e.target.value })
                );
              }
            }}
            onBlur={() => onAddOptionInputBlur?.(option.value.toString())}
            value={option.label}
            autoFocus
            // onKeyDown={onAddOptionInputKeyDown}
          />
        )}
      </StyledFormItem>
      <StyledFormItem
        name={`choiceType_${option.value}`}
        label={
          <FormattedMessage id="rich_text_editor.sections.choices_answer_type" />
        }
      >
        <Select
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            container: (base) => ({ ...base, width: '100%' }),
          }}
          id={`choiceType_${option.value}`}
          options={choiceTypeList as Option[]}
          value={
            choiceTypeList.find(
              (type) => type.value === option.fieldOptionMeta?.optionType,
            ) as Option
          }
          onChange={(value) => {
            if (!value || Array.isArray(value)) {
              return;
            }

            const selectedValue = value as Option;

            if (form) {
              form.setFieldsValue({
                [`choiceType_${option.value}`]: selectedValue,
              });
            }

            if (editInputOption) {
              const booleanChoices = [
                { value: 'Yes', label: 'Yes' },
                { value: 'No', label: 'No' },
              ];
              const newChoices =
                selectedValue.value === TableDataType.BOOLEAN
                  ? booleanChoices
                  : [];

              if (selectedValue.value === TableDataType.BOOLEAN) {
                setDataChoices(booleanChoices);
              } else {
                setDataChoices([]);
              }

              const nextType = selectedValue.value as TableDataType;

              editInputOption({
                ...option,
                fieldOptionMeta: {
                  ...option.fieldOptionMeta,
                  optionType: nextType,
                  choices: newChoices,
                  answerLength:
                    nextType === TableDataType.TEXT
                      ? (option.fieldOptionMeta?.answerLength ??
                        AnswerLength.TwentyWords)
                      : null,
                },
              });
            }
          }}
        />
      </StyledFormItem>
      <StyledFormItem
        name={`aiContext_${option.value}`}
        label={
          <FormattedMessage id="rich_text_editor.sections.question_section.add_ai_context" />
        }
      >
        <TextAreaContainer
          id={`aiContext_${option.value}`}
          ref={aiContextRef}
          value={option.fieldOptionMeta?.aiContext || ''}
          onChange={(e) => {
            if (form) {
              form.setFieldsValue({
                aiContext: e.target.value,
              });
            }

            editInputOption({
              ...option,
              fieldOptionMeta: {
                ...option.fieldOptionMeta,
                aiContext: e.target.value,
              },
            });
          }}
          placeholder={contextPlaceholder}
        />
      </StyledFormItem>

      {[
        TableDataType.SingleSelect,
        TableDataType.MultiSelect,
        TableDataType.BOOLEAN,
      ].includes(option.fieldOptionMeta?.optionType) ? (
        <Form.Item label={choicesLabel}>
          <StyledChoicesWrapper>
            {option.fieldOptionMeta?.choices && (
              <SortableOptions
                inputType="text"
                shouldDrag={false}
                options={option.fieldOptionMeta?.choices ?? []}
                removeOption={removeDataOption}
                updateOptions={updateDataOptions}
                editInputOption={updateDataOptions}
              />
            )}
            {[TableDataType.SingleSelect, TableDataType.MultiSelect].includes(
              option.fieldOptionMeta?.optionType,
            ) ? (
              <>
                <AddOptionsInput
                  shouldValidate={false}
                  existingOptions={option.fieldOptionMeta?.choices ?? []}
                  addOption={addDataOption}
                  addedAsFormItem={false}
                />
                <Form.Item name="aiContextChecked">
                  <StyledCheckboxContainer>
                    <Checkbox
                      color="black"
                      label={aiGeneratedChoicesLabel}
                      checked={option.fieldOptionMeta?.generateNewChoices}
                      onCheckedChange={(checked) => {
                        editInputOption({
                          ...option,
                          fieldOptionMeta: {
                            ...option.fieldOptionMeta,
                            generateNewChoices: checked,
                          },
                        });
                      }}
                      helperText={aiGeneratedChoicesPlaceholder}
                    />
                  </StyledCheckboxContainer>
                </Form.Item>
              </>
            ) : (
              <></>
            )}
          </StyledChoicesWrapper>
        </Form.Item>
      ) : (
        <></>
      )}
      <AnswerDropdowns
        option={option}
        editInputOption={editInputOption}
        setChoices={setChoices}
      />

      <Divider />
    </FieldRow>
  );
};

const QuestionChoice = ({
  labelText,
  options,
  removeOption,
  validateForm,
  editInputOption,
  inputType,
  setChoices,
  focusOptionValue,
  onFocusOptionDone,
  addQuestionPlaceholder,
}) => {
  const [form] = Form.useForm();

  // Validate on mount and when options change so empty question labels show error and disable Done
  useEffect(() => {
    const hasEmptyLabel = options.some((opt) => !opt.label?.trim());

    if (hasEmptyLabel) {
      queueMacrotask(() => {
        form.validateFields().catch((error) => {
          error?.errorFields?.forEach(
            (field: { name: string[]; errors: string[] }) => {
              form.setFields([{ name: field.name, errors: field.errors }]);
            },
          );
        });
      });
    }
  }, [options, form]);

  const onAddOptionInputBlur = useCallback(
    (name: string) => {
      const value = form.getFieldValue(name);

      if (value) {
        form.setFields([
          {
            name,
            errors: [],
          },
        ]);
      }
    },
    [form],
  );

  return (
    <StyledChoicesContainer className="choices-container">
      <Form
        form={form}
        layout="vertical"
        onFieldsChange={() => {
          validateForm?.(form);
        }}
      >
        {options.map((option, index) => (
          <FormOption
            labelText={labelText}
            key={option.value.toString()}
            index={index}
            inputType={inputType}
            option={option}
            removeOption={() => {
              removeOption(option);
              form.setFields([
                {
                  name: option.value.toString(),
                  errors: [],
                },
              ]);
              validateForm?.(form);
            }}
            editOption={
              editInputOption ? () => editInputOption(option) : undefined
            }
            editInputOption={editInputOption}
            onAddOptionInputBlur={onAddOptionInputBlur}
            form={form}
            validateForm={validateForm}
            setChoices={setChoices}
            focusOptionValue={focusOptionValue}
            onFocusOptionDone={onFocusOptionDone}
            addQuestionPlaceholder={addQuestionPlaceholder}
          />
        ))}
      </Form>
    </StyledChoicesContainer>
  );
};

export const PromptInput = ({
  prompt,
  setPrompt,
  onGenerate,
  onCancel,
  onUndo,
  allowResolve,
  isActionLoading,
  getNodeAsPayload,
  pageType,
  pageTriggerListFilter,
}: {
  prompt: any;
  setPrompt: any;
  onGenerate: any;
  onCancel?: any;
  onUndo?: any;
  allowResolve?: any;
  isActionLoading?: boolean;
  getNodeAsPayload?: any;
  pageType?: any;
  pageTriggerListFilter?: GroupedFilter;
}) => {
  const [form] = Form.useForm<{
    title: string;
    aiContextChecked: boolean;
    aiContext: string;
  }>();
  const [showNext, setShowNext] = useState(!prompt.title?.trim());
  const titlePlaceholder = useTranslation(
    'rich_text_editor.sections.extract_info_section.title_placeholder',
  );
  const titleRef = useRef<HTMLInputElement>(null);

  const getChoices = () => {
    return prompt.questions.length > 0
      ? prompt.questions.map((question) => {
          const optionType =
            question.fieldOptionMeta?.optionType ?? TableDataType.TEXT;

          return {
            label: question.label,
            value: question.value,
            position: question.position,
            fieldOptionMeta: {
              ...question.fieldOptionMeta,
              choices: question.fieldOptionMeta?.choices ?? [],
              aiContext: question.fieldOptionMeta.aiContext,
              id: question.fieldOptionMeta.id,
              answerFrom:
                question.fieldOptionMeta.answerFrom ?? answerFrom.Customer,
              answerLength:
                optionType === TableDataType.TEXT
                  ? (question.fieldOptionMeta.answerLength ??
                    AnswerLength.TwentyWords)
                  : null,
              optionType,
            },
          };
        })
      : [
          {
            label: '',
            value: 1,
            position: 1,
            fieldOptionMeta: {
              optionType: TableDataType.TEXT,
              aiContext: '',
              id: uuidv4(),
              answerFrom: answerFrom.Customer,
              answerLength: AnswerLength.TwentyWords,
            },
          },
        ];
  };
  const [choices, setChoices] = useState(getChoices());

  useEffect(() => {
    form.setFieldsValue({
      title: prompt.title,
      aiContextChecked: prompt.aiContextChecked,
      aiContext: prompt.aiContext,
    });
  }, [
    form,
    prompt.aiContext,
    prompt.aiContextChecked,
    prompt.question,
    prompt.title,
  ]);

  useEffect(() => {
    queueMacrotask(() => {
      titleRef.current?.select();
    });
  }, []);

  const [lastAddedOptionValue, setLastAddedOptionValue] = useState<
    number | null
  >(null);

  const addOption = useCallback(
    (option) => {
      const newValue =
        choices.reduce((max, opt) => Math.max(max, opt.value), 0) + 1;
      const newOptions = choices.concat({
        ...option,
        label: option.label ?? '',
        value: newValue,
        position: choices.length + 1,
        fieldOptionMeta: {
          optionType: TableDataType.TEXT,
          id: uuidv4(),
          answerLength: AnswerLength.TwentyWords,
          answerFrom: answerFrom.Customer,
        },
      });

      setLastAddedOptionValue(newValue);
      setChoices(newOptions);
    },
    [choices],
  );
  const removeOption = useCallback(
    (option) => {
      const newOptions = choices.filter((x) => x.value !== option.value);

      setChoices(
        newOptions.map((x, index) => ({
          ...x,
          position: index + 1,
        })),
      );
    },
    [choices, setChoices],
  );
  const updateOptions = (options) => {
    const existingChoice = choices.find(
      (existing) => existing.value === options.value,
    );

    if (existingChoice) {
      setChoices((previous) => {
        return previous.map((choice) => {
          if (choice?.value === existingChoice?.value) {
            return options;
          }

          return choice;
        });
      });
    }
  };

  const triggerGenerate = () => {
    const questions = choices.map((row) => {
      const optionType = row.fieldOptionMeta?.optionType ?? TableDataType.TEXT;

      return {
        ...row,
        fieldOptionMeta: {
          ...row.fieldOptionMeta,
          answerLength:
            optionType === TableDataType.TEXT
              ? (row.fieldOptionMeta.answerLength ?? AnswerLength.TwentyWords)
              : null,
        },
      };
    });

    const promptHash = {
      ...prompt,
      title: form.getFieldValue('title'),
      aiContextChecked: form.getFieldValue('aiContextChecked'),
      aiContext: form.getFieldValue('aiContext'),
      questions,
    };

    setPrompt(promptHash);
    onGenerate(promptHash);
  };

  const [errors, setErrors] = useState<boolean>(false);

  const validateForm = (formData: any) => {
    const errors = formData.getFieldsError();

    let showError = false;

    errors.forEach((error: any) => {
      if (error.errors.length > 0 && !showError) {
        showError = true;
      }
    });
    setErrors(showError);
  };

  const labelText = useTranslation(
    'rich_text_editor.sections.question_section.question',
  );

  const hasEmptyQuestionLabel = choices.some((c) => !c.label?.trim());
  const isDisabled = errors || hasEmptyQuestionLabel;

  const addQuestionText = useTranslation('add_question');

  return (
    <SectionCard>
      <SectionHeading
        title="editor.sections.multi_question_section.title"
        onClose={onUndo}
        showCloseButton={!isDisabled}
      />
      <SectionWrapper>
        <Form form={form} layout="vertical">
          <StyledTitleFormItem
            name="title"
            label={
              <FormattedMessage id="rich_text_editor.sections.question_section.title" />
            }
          >
            <InputField
              type="text"
              ref={titleRef}
              onChange={(e) => {
                setPrompt({
                  ...prompt,
                  title: e.target.value,
                });
              }}
              placeholder={titlePlaceholder}
              autoFocus
            />
          </StyledTitleFormItem>

          {!showNext && (
            <StyledChoicesContainer>
              <QuestionChoice
                labelText={labelText}
                options={choices}
                removeOption={removeOption}
                editInputOption={updateOptions}
                validateForm={validateForm}
                inputType="textarea"
                setChoices={setChoices}
                focusOptionValue={lastAddedOptionValue}
                onFocusOptionDone={() => setLastAddedOptionValue(null)}
                addQuestionPlaceholder={addQuestionText}
              />
              <AddOptionsInput
                placeholderText={addQuestionText}
                shouldValidate={false}
                existingOptions={choices}
                addOption={addOption}
                addedAsFormItem={false}
              />
            </StyledChoicesContainer>
          )}
        </Form>
        {showNext ? (
          <ConfigurationBarWrapper>
            <Button
              variant="primary"
              disabled={!prompt.title?.trim()}
              onClick={() => {
                setShowNext(false);
              }}
            >
              <FormattedMessage id="next" />
            </Button>
          </ConfigurationBarWrapper>
        ) : (
          <>
            <SectionDivider />
            <ConfigurationBarWrapper hasOptions={true}>
              <SharedConfigurationBar
                prompt={prompt}
                setPrompt={setPrompt}
                isActionLoading={isActionLoading}
                onCancel={onCancel}
                onSubmit={triggerGenerate}
                validation={{
                  isDisabled,
                }}
                getNodeAsPayload={getNodeAsPayload}
                allowResolve={allowResolve}
                actionBarConfig={{
                  canShowPreviewButton: false,
                  canWrapTrashButton: true,
                  canShowStreaming: true,
                }}
                pageType={pageType}
                pageTriggerListFilter={pageTriggerListFilter}
              />
            </ConfigurationBarWrapper>
          </>
        )}
      </SectionWrapper>
    </SectionCard>
  );
};
